/**
 * Address Info Mock Data Panel
 *
 * 开发环境下的快捷填充工具
 * 提供可视化的面板用于快速填充地址信息数据
 *
 * @module mockDataGenerator.demo
 */
import { triggerFileUpload } from "../../utils/mockFileUploadTrigger";
import { Button, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState } from "react";
import {
  addressMockDataPresets,
  generateAddressInfoMockData,
} from "./mockDataGenerator.address-info";
import { delay } from "@/utils";

interface AddressInfoMockDataPanelProps {
  /** 表单实例 */
  form?: FormInstance;
}

/**
 * 地址信息 Mock 数据生成工具面板
 */
export function AddressInfoMockDataPanel({
  form,
}: AddressInfoMockDataPanelProps) {
  const [visible, setVisible] = useState(false);

  if (!form) {
    return null;
  }

  /* ============================================================
   * 预设场景操作
   * ========================================================== */

  const handleFillHkUtilityBill = () =>
    handleFillWithFileUpload(
      addressMockDataPresets.hkResidentWithUtilityBill,
      "✅ 已填充香港居民（水电单）+上传文件:"
    );

  const handleFillMainlandBankStatement = () =>
    handleFillWithFileUpload(
      addressMockDataPresets.mainlandChineseWithBankStatement,
      "✅ 已填充中国大陆居民（银行对账单）+上传文件:"
    );

  const handleFillForeignRental = () =>
    handleFillWithFileUpload(
      addressMockDataPresets.foreignResidentWithRental,
      "✅ 已填充外国居民（租赁合同）+上传文件:"
    );

  const handleFillHotelLease = () =>
    handleFillWithFileUpload(
      addressMockDataPresets.hotelApartmentLease,
      "✅ 已填充酒店公寓租赁+上传文件:"
    );

  const handleFillRandom = () =>
    handleFillWithFileUpload(
      generateAddressInfoMockData,
      "✅ 已填充随机地址数据+上传文件:"
    );

  const handleClear = () => {
    form.resetFields();
    console.log("✅ 已清空地址信息表单");
  };

  /**
   * 触发文件上传控件
   * 模拟用户点击上传按钮
   */
  const handleTriggerFileUpload = async () => {
    message.loading({
      content: "正在准备文件...",
      key: "upload",
      duration: 0,
    });

    const success = await triggerFileUpload({
      fileUrl: "/icon.png",
      fileName: "address-proof.png",
      onSuccess: (file) => {
        message.success({
          content: "✅ 已触发文件上传",
          key: "upload",
          duration: 2,
        });
        console.log("✅ 已触发文件上传:", file);
      },
      onError: (error) => {
        message.error({
          content: "❌ 触发文件上传失败",
          key: "upload",
          duration: 2,
        });
        console.error("❌ 触发文件上传失败:", error);
      },
    });

    if (!success) {
      message.error({
        content: "❌ 触发文件上传失败",
        key: "upload",
        duration: 2,
      });
    }
  };

  /**
   * 填充数据并触发文件上传
   */
  const handleFillWithFileUpload = async (
    dataGenerator: () => any,
    logMessage: string
  ) => {
    // 1. 先填充基础数据
    const data = dataGenerator();
    form.setFieldsValue(data);
    console.log(logMessage, data);

    // 2. 等待表单更新
    await delay(100);

    // 3. 触发文件上传
    await handleTriggerFileUpload();
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            🏠
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              🏠 地址信息 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                预设场景
              </div>
              <Button size="small" block onClick={handleFillHkUtilityBill}>
                香港居民（水电单）
              </Button>
              <Button
                size="small"
                block
                onClick={handleFillMainlandBankStatement}
              >
                大陆居民（银行对账单）
              </Button>
              <Button size="small" block onClick={handleFillForeignRental}>
                外国居民（租赁合同）
              </Button>
              <Button size="small" block onClick={handleFillHotelLease}>
                酒店公寓租赁
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 其他操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                其他操作
              </div>
              <Button size="small" block onClick={handleFillRandom}>
                随机生成
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 文件上传 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                📎 文件上传
              </div>
              <Button
                size="small"
                block
                type="primary"
                onClick={handleTriggerFileUpload}
              >
                触发上传控件
              </Button>
              <Button
                size="small"
                block
                type="primary"
                onClick={() =>
                  handleFillWithFileUpload(
                    addressMockDataPresets.hkResidentWithUtilityBill,
                    "✅ 已填充香港居民（水电单）+触发上传:"
                  )
                }
              >
                香港居民 + 上传
              </Button>
            </div>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClear}>
              清空表单
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default AddressInfoMockDataPanel;
